<script lang="ts">
	import type { WithElementRef } from 'bits-ui';

	import { browser } from '$app/environment';
	import { afterNavigate } from '$app/navigation';
	import type { HTMLAttributes } from 'svelte/elements';

	import * as Tooltip from '$lib/components/ui/tooltip';

	import { cn } from '$lib/utils';

	import {
		SIDEBAR_COOKIE_MAX_AGE,
		SIDEBAR_COOKIE_NAME,
		SIDEBAR_WIDTH,
		SIDEBAR_WIDTH_ICON
	} from './constants';
	import { setSidebar } from './context.svelte';

	let {
		ref = $bindable(null),
		open = $bindable(getDefaultOpen()),
		onOpenChange = () => {},
		controlledOpen = false,
		class: className,
		style,
		children,
		...restProps
	}: WithElementRef<HTMLAttributes<HTMLDivElement>> & {
		open?: boolean;
		onOpenChange?: (open: boolean) => void;
		controlledOpen?: boolean;
	} = $props();

	function getDefaultOpen() {
		if (!browser) {
			return true;
		}

		const cookies = browser
			? document.cookie.split(';').map((cookie) => {
					const [key, value] = cookie.trim().split('=');
					return { key, value };
				})
			: [];
		const cookie = cookies.find((cookie) => cookie.key === SIDEBAR_COOKIE_NAME);
		if (!cookie) {
			return true;
		}

		return cookie.value === 'true';
	}

	const sidebar = setSidebar({
		open: () => open,
		setOpen: (value: boolean) => {
			if (controlledOpen) {
				onOpenChange(value);
			} else {
				open = value;
				onOpenChange(value);
			}

			// This sets the cookie to keep the sidebar state.
			document.cookie = `${SIDEBAR_COOKIE_NAME}=${open}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}; samesite=strict`;
		}
	});

	afterNavigate(() => {
		sidebar.openMobile = false;
	});
</script>

<svelte:window onkeydown={sidebar.handleShortcutKeydown} onkeyup={sidebar.handleShortcutKeyup} />

<Tooltip.Provider delayDuration={0}>
	<div
		style="--sidebar-width: {SIDEBAR_WIDTH}; --sidebar-width-icon: {SIDEBAR_WIDTH_ICON}; {style}"
		class={cn(
			'group/sidebar-wrapper has-data-[variant=inset]:bg-sidebar flex min-h-svh w-full',
			className
		)}
		bind:this={ref}
		{...restProps}
	>
		{@render children?.()}
	</div>
</Tooltip.Provider>
